import { G } from '@mobily/ts-belt';
import Warning from '@mui/icons-material/Warning';
import { Box, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
// Button without permissions
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Button, useTranslate } from 'react-admin';
import { FieldValues } from 'react-hook-form';

import {
  B2bPlanTypeEsPeiProfile,
  B2bPlanTypeEsPeiProfileValues,
  B2bPlanTypeEsPerecoiPilote,
  B2bPlanTypeEsPerecoiPiloteValues,
} from '@Common/types/b2b/plans';

import { ExportAllocations } from '@sh4res/bo-common/components/B2bCampaigns/ExportAllocations';
import { AllocationRecord } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/AllocationsConfirmationStep';
import { DialogTitleWithX } from '@sh4res/bo-common/components/DialogTitleWithX/DialogTitleWithX';
import { ConfirmationModalDetails } from '@sh4res/bo-common/components/StepperForm';
import { TranslationKeys } from '@sh4res/bo-common/i18n/types';

export const shouldConfirmConsultationPeriodLength = (
  values: FieldValues,
  translate: ReturnType<typeof useTranslate>,
): ConfirmationModalDetails => {
  const {
    start_date: startDate,
    end_date: endDate,
    choice_collected_by: choiceCollectedBy,
  } = values;

  if (choiceCollectedBy !== 'distributor') {
    return { shouldConfirm: false };
  }

  const start = new Date(startDate);
  const end = new Date(endDate);
  const durationDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

  if (durationDays < 15) {
    return {
      blocking: false,
      title: (
        <Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Warning style={{ color: '#999999' }} />
            {translate('resources.bo_common.b2b.campaigns.sections.duration_warning_modal.title')}
          </Box>
        </Typography>
      ),
      body: (
        <Typography variant="body1">
          {translate('resources.bo_common.b2b.campaigns.sections.duration_warning_modal.body')}
        </Typography>
      ),
      shouldConfirm: true,
    };
  }

  return {
    shouldConfirm: false,
  };
};

const renderModalBody = (
  translate: ReturnType<typeof useTranslate>,
  prefix: 'company_collected_choices_blocking' | 'company_collected_choices' | 'removed_employees',
  list: { identifier: string | null; firstname: string | null; lastname: string | null }[],
  blocking: boolean,
): ConfirmationModalDetails => ({
  blocking,
  title: (
    <Typography variant="h6">
      {translate(`resources.bo_common.b2b.campaigns.sections.${prefix}.title`)}
    </Typography>
  ),
  body: (
    <Box>
      <Typography variant="body1" sx={{ paddingTop: '10px' }}>
        {translate(`resources.bo_common.b2b.campaigns.sections.${prefix}.body`)}
      </Typography>
      <Typography variant="body1" sx={{ paddingTop: '10px' }}>
        {translate(`resources.bo_common.b2b.campaigns.sections.${prefix}.affiliates`)} {list.length}
      </Typography>
      <Box
        sx={{
          bgcolor: '#FAFAFA',
          border: '1px solid #E0E0E0',
          borderRadius: 2,
          padding: 2,
          maxHeight: 220,
          overflow: 'auto',
          fontSize: '14px',
          color: '#000000DE',
          marginTop: '10px',
        }}>
        {[
          'identifier,firstname,lastname',
          ...list.map((reg) => `${reg.identifier},${reg.firstname},${reg.lastname}`),
        ].map((line, idx) => (
          <Typography
            key={idx}
            component="pre"
            variant="body2"
            sx={{ display: 'flex', gap: 2, mb: 1, margin: 0 }}>
            {idx + 1}
            <span>{line}</span>
          </Typography>
        ))}
      </Box>
    </Box>
  ),
  shouldConfirm: true,
});

/**
 * A zero amount on an ongoing campaign drops the employee from the payload, and so from the
 * operation. Confirm it explicitly, since it is otherwise indistinguishable from a typo.
 */
export const shouldConfirmRemovedEmployees = (
  values: FieldValues,
  translate: ReturnType<typeof useTranslate>,
): ConfirmationModalDetails => {
  const removed = (values.allocations ?? [])
    .filter(
      (allocation: AllocationRecord) =>
        allocation.amount === 0 && G.isNotNullable(allocation.employee_id),
    )
    .map(({ identifier, firstname, lastname }: AllocationRecord) => ({
      identifier,
      firstname,
      lastname,
    }));

  return removed.length > 0
    ? renderModalBody(translate, 'removed_employees', removed, false)
    : { shouldConfirm: false };
};

export const shouldConfirmCompanyCollectedChoices = (
  values: FieldValues,
  translate: ReturnType<typeof useTranslate>,
): ConfirmationModalDetails => {
  const allocations = (values.allocations ?? []) as AllocationRecord[];

  if (values.choice_collected_by !== 'company') {
    return { shouldConfirm: false };
  }

  const duplicates = allocations
    .filter((allocation) => {
      const planIdCounts = new Map<string, number>();
      allocation.company_collected_choices?.forEach((choice) => {
        if (choice.allocation_type === 'managed_plan' && typeof choice.plan_id === 'number') {
          const planId = `${choice.plan_id}${choice.deductibility ? `_${choice.deductibility}` : ''}`;
          planIdCounts.set(planId, (planIdCounts.get(planId) ?? 0) + 1);
        }
      });
      return [...planIdCounts.values()].some((count) => count > 1);
    })
    .map(({ nir, ntt, firstname, lastname, identifier, employee_id }) => ({
      nir,
      ntt,
      firstname,
      lastname,
      identifier,
      employee_id,
    }));

  if (duplicates.length > 0) {
    return renderModalBody(translate, 'company_collected_choices_blocking', duplicates, true);
  }

  const changes = allocations
    .filter((allocation) =>
      allocation.company_collected_choices?.some((choice) => {
        if (choice.allocation_type !== 'managed_plan' || !choice.risk_profile) {
          return false;
        }
        if (
          B2bPlanTypeEsPerecoiPiloteValues.includes(
            choice.plan_type as B2bPlanTypeEsPerecoiPilote,
          ) &&
          choice.risk_profile !== allocation.investor_profile
        ) {
          return true;
        }
        if (
          B2bPlanTypeEsPeiProfileValues.includes(choice.plan_type as B2bPlanTypeEsPeiProfile) &&
          choice.risk_profile !== allocation.pei_investor_profile
        ) {
          return true;
        }
        return false;
      }),
    )
    .map(({ nir, ntt, firstname, lastname, identifier, employee_id }) => ({
      nir,
      ntt,
      firstname,
      lastname,
      identifier,
      employee_id,
    }));

  if (changes.length > 0) {
    return renderModalBody(translate, 'company_collected_choices', changes, false);
  }

  return { shouldConfirm: false };
};

type ConfirmationDialogDetails = {
  title: TranslationKeys;
  body: TranslationKeys;
  bodyArgs?: Record<string, unknown>;
  campaignId: number;
  showAllocations: boolean;
};
export type ConfirmationDialogOpen = (details: ConfirmationDialogDetails) => void;
export const useCampaignConfirmationDialog = () => {
  const [details, setDetails] = useState<ConfirmationDialogDetails | null>(null);
  const translate = useTranslate();

  const open = useCallback(
    (details: ConfirmationDialogDetails) => {
      setDetails(details);
    },
    [setDetails],
  );

  const close = useCallback(() => {
    setDetails(null);
  }, [setDetails]);

  return {
    open,
    Dialog: (
      <Dialog open={details !== null} onClose={close} maxWidth="sm" fullWidth>
        {details && (
          <>
            <DialogTitleWithX onClose={close}>{translate(details.title)}</DialogTitleWithX>

            <DialogContent>
              <Typography variant="body1">{translate(details.body, details.bodyArgs)}</Typography>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                {details.showAllocations && <ExportAllocations campaignId={details.campaignId} />}
              </Box>
            </DialogContent>

            <DialogActions>
              <Button
                variant="contained"
                onClick={close}
                label="resources.bo_common.b2b.campaigns.sections.success_modal.btn"
                data-testid="BoExternalB2bCampaigns_ConfirmButton"
              />
            </DialogActions>
          </>
        )}
      </Dialog>
    ),
  };
};
